import { useAuth0 } from "@auth0/auth0-react";
import SettingsIcon from "@mui/icons-material/Settings";
import { Box, FormControlLabel, IconButton, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts";
import { useEffect, useMemo, useState } from "react";
import useColors from "../../../../../../../api/hooks/useColors";
import CustomSwitch from "../../../../../../../components/CustomSwitch";
import CustomTooltip from "../../../../../../../components/CustomTooltip";
import ClearFiltersButton from "../../../../../../../components/filters/ClearFiltersButton";
import SvgToIcon from "../../../../../../../icons/SvgToIcon";
import { Platforms } from "../../../../../../../utils/constants";
import { logos } from "../../../../icons/iconExport";
import OSVersionManagementChartLegend from "./OSVersionManagementChartLegend";
import OSVersionSettingsDialog from "./OSVersionSettingsDialog";
import OsCustomBarChart from "./OsCustomBarChart";
import {
  createDataset,
  createMapFullMacOsToVersion,
  createMapFullWindowsOsToVersion,
  getDefaultVersionSettings,
  getMacOSName,
  getWindowsOSName,
  isMacOSVersionSupported,
  isWindowsVersionSupported,
  processEntities,
  windowsOsVersionMap,
} from "./osVersionHelpers";

const OSVersionManagementChart = ({
  entities,
  appliedFilters,
  setAppliedFilters,
}) => {
  const colors = useColors();
  const [latestToggleOn, setLatestToggleOn] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [versionSettings, setVersionSettings] = useState(
    getDefaultVersionSettings()
  );
  const { user } = useAuth0();

  const fullOSToNameMac = {};
  const fullOSToNameWindows = {};

  // Extract all unique Windows versions from the entity data
  const allWindowsVersions = useMemo(() => {
    const versions = new Set();

    entities
      .filter(
        (entity) => entity.platform === Platforms.WINDOWS && entity.os_version
      )
      .forEach((entity) => {
        const osVersionParts = entity.os_version.split(".");
        if (osVersionParts.length >= 3) {
          const formattedVersion = `${osVersionParts[0]}.${osVersionParts[1]}.${osVersionParts[2]}`;
          versions.add(formattedVersion);
        }
      });

    return Array.from(versions);
  }, [entities]);

  // Update version settings when new Windows versions are detected
  useEffect(() => {
    if (allWindowsVersions.length > 0) {
      // Create updated settings including any new Windows versions
      const updatedSettings = { ...versionSettings };
      let hasNewVersions = false;

      allWindowsVersions.forEach((version) => {
        // If this version isn't in our settings yet, add it
        if (!updatedSettings.windows.hasOwnProperty(version)) {
          // Set initial value - enabled if it's in windowsOsVersionMap, otherwise disabled
          updatedSettings.windows[version] =
            !!windowsOsVersionMap[version] &&
            !updatedSettings.windows[version].enabled;
          hasNewVersions = true;
        }
      });

      // Only update state if we found new versions
      if (hasNewVersions) {
        setVersionSettings(updatedSettings);
      }
    }
  }, [allWindowsVersions]);

  const macOSVersionCounts = useMemo(
    () =>
      processEntities(
        entities.filter((entity) => entity.platform === Platforms.MACOS),
        Platforms.MACOS,
        (version) => getMacOSName(version, latestToggleOn, versionSettings),
        (version) =>
          isMacOSVersionSupported(version, latestToggleOn, versionSettings),
        createMapFullMacOsToVersion,
        latestToggleOn,
        fullOSToNameMac,
        versionSettings
      ),
    [entities, latestToggleOn, versionSettings]
  );

  const windowsVersionCounts = useMemo(
    () =>
      processEntities(
        entities.filter((entity) => entity.platform === Platforms.WINDOWS),
        Platforms.WINDOWS,
        (version) => getWindowsOSName(version, latestToggleOn, versionSettings),
        (version) =>
          isWindowsVersionSupported(version, latestToggleOn, versionSettings),
        createMapFullWindowsOsToVersion,
        latestToggleOn,
        fullOSToNameWindows,
        versionSettings
      ),
    [entities, latestToggleOn, versionSettings]
  );

  const macOSDataset = useMemo(
    () => createDataset(macOSVersionCounts, Platforms.MACOS, colors),
    [macOSVersionCounts]
  );
  const windowsDataset = useMemo(
    () => createDataset(windowsVersionCounts, Platforms.WINDOWS, colors),
    [windowsVersionCounts]
  );

  const sortDataset = (dataset) => {
    dataset.sort((a, b) => {
      if (a.os.includes("Older Versions")) return 1;
      if (b.os.includes("Older Versions")) return -1;
      return a.platform.localeCompare(b.platform);
    });
  };

  useMemo(() => {
    sortDataset(macOSDataset);
    sortDataset(windowsDataset);
  }, [macOSDataset, windowsDataset, latestToggleOn]);

  // Determine the maximum count value from both datasets
  const maxCount = useMemo(
    () =>
      Math.max(
        ...windowsDataset.map((item) => item.count),
        ...macOSDataset.map((item) => item.count)
      ) * 1.1,
    [windowsDataset, macOSDataset]
  );

  const ChartTitle = () => {
    const allOlderVersionsNumber = [...macOSDataset, ...windowsDataset]
      .filter((item) => item.olderVersion)
      .map((item) => item.count)
      .reduce((a, b) => (a > 1 ? a : 0) + (b > 1 ? b : 0), 0);
    const entitiesOsMacAndWindows = entities.filter(
      (entity) =>
        entity.platform === Platforms.MACOS ||
        entity.platform === Platforms.WINDOWS
    );
    const updateDevices =
      entitiesOsMacAndWindows.length - allOlderVersionsNumber;
    const updateDevicesPercentage =
      (updateDevices / entitiesOsMacAndWindows.length) * 100;
    return (
      <Box
        sx={{
          color: colors.text_primary,
        }}
      >
        <Typography variant="overline">
          {updateDevicesPercentage.toFixed(1)}% {" (" + updateDevices + "/"}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: colors.text_secondary,
          }}
        >
          {entitiesOsMacAndWindows.length}
        </Typography>
        <Typography variant="overline">{")"}</Typography>
        <Typography variant="caption">{" Devices are Up-to-date"}</Typography>
      </Box>
    );
  };

  const handleVersionSettingsSave = (newSettings) => {
    setVersionSettings(newSettings);
  };

  return (
    <Box sx={{ p: 3 }}>
      {entities.length === 0 ? (
        <Box>
          <Typography variant="subtitle1">
            OS Version Distribution on Endpoints
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            background: colors.common_white,
            boxShadow: "0px 0px 6.3px 0px " + colors.grey_24percent,
            borderRadius: "8px",
            width: "100%",
            height: "400px",
            p: 2,
          }}
        >
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography variant="subtitle1">
                OS Version Distribution on Endpoints
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <ClearFiltersButton
                  appliedFilters={appliedFilters}
                  setAppliedFilters={setAppliedFilters}
                />
                {(user.email.includes("breeze") ||
                  user.email.includes("pagaya")) && (
                  <>
                    <FormControlLabel
                      sx={{
                        background: colors.common_white,
                        color: colors.contrast_text_dark,
                        height: "32px",
                        textTransform: "unset",
                        borderRadius: "8px",
                        margin: "0",
                        gap: 1,
                        padding: "8px",
                      }}
                      control={
                        <CustomSwitch
                          checked={latestToggleOn}
                          onChange={() => setLatestToggleOn(!latestToggleOn)}
                        />
                      }
                      label={
                        <Typography variant="caption">
                          Aggregate supported versions
                        </Typography>
                      }
                      labelPlacement="end"
                    />
                    <CustomTooltip
                      title={
                        latestToggleOn
                          ? "Configure version settings"
                          : "Only available when aggregation is enabled"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => setSettingsOpen(true)}
                          disabled={!latestToggleOn}
                          sx={{
                            color: latestToggleOn
                              ? colors.text_secondary
                              : colors.grey_48percent,
                            "&:hover": {
                              color: latestToggleOn
                                ? colors.primary
                                : colors.grey_48percent,
                              backgroundColor: latestToggleOn
                                ? colors.grey_12percent
                                : "transparent",
                            },
                            padding: "6px",
                            borderRadius: "6px",
                          }}
                        >
                          <SettingsIcon
                            fontSize="small"
                            sx={{ fontSize: 18 }}
                          />
                        </IconButton>
                      </span>
                    </CustomTooltip>
                  </>
                )}
              </Box>
            </Box>
            <ChartTitle />
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                pt: 1,
              }}
            >
              <Box
                sx={{
                  width: "90%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <SvgToIcon IconLogo={logos.WindowsIcon} />
                    <Typography variant="caption">Windows</Typography>
                  </Box>
                  <OsCustomBarChart
                    dataset={windowsDataset}
                    maxCount={maxCount}
                    appliedFilters={appliedFilters}
                    setAppliedFilters={setAppliedFilters}
                    fullOSToName={fullOSToNameWindows}
                    latestToggleOn={latestToggleOn}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mt: -5,
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <SvgToIcon IconLogo={logos.MacIcon} />
                    <Typography variant="caption">macOS</Typography>
                  </Box>
                  <OsCustomBarChart
                    dataset={macOSDataset}
                    maxCount={maxCount}
                    appliedFilters={appliedFilters}
                    setAppliedFilters={setAppliedFilters}
                    fullOSToName={fullOSToNameMac}
                    latestToggleOn={latestToggleOn}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    mt: -32.2,
                  }}
                >
                  <BarChart
                    sx={{
                      "& .MuiChartsAxis-tick": {
                        strokeWidth: 0,
                      },
                      "& .MuiChartsAxis-left .MuiChartsAxis-line": {
                        strokeWidth: 0,
                      },

                      "& .MuiChartsAxis-bottom .MuiChartsAxis-line": {
                        strokeWidth: 0,
                      },

                      "& .MuiChartsAxisHighlight-root": {
                        fill: "none",
                      },
                      "& .MuiChartsAxis-root .MuiChartsAxis-tickLabel": {
                        fill: colors.text_secondary,
                      },
                    }}
                    dataset={[
                      {
                        os: "",
                        count: 0,
                      },
                    ]}
                    yAxis={[
                      {
                        scaleType: "band",
                        dataKey: "os",
                      },
                    ]}
                    xAxis={[
                      {
                        max: maxCount,
                      },
                    ]}
                    tooltip={{
                      trigger: "item",
                      itemContent: (value) => null,
                    }}
                    series={[{ dataKey: "count" }]}
                    layout="horizontal"
                    height={270}
                    margin={{
                      top: 0,
                      left: 180,
                    }}
                    grid={{ vertical: true }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      mt: -6.5,
                      color: colors.text_secondary,
                    }}
                  >
                    Devices
                  </Typography>
                </Box>
              </Box>
              <OSVersionManagementChartLegend />
            </Box>
          </Box>
        </Box>
      )}
      <OSVersionSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={versionSettings}
        onSave={handleVersionSettingsSave}
      />
    </Box>
  );
};

export default OSVersionManagementChart;
